import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import { useResponseItemContext } from '@app/src/context/ResponseItemContext'
import { useProviderReport } from '@app/src/pages/Questionnaire/Provider/ProviderReportContext'
import type { AiSuggestion } from '@app/src/pages/Questionnaire/hooks/useFetchFileAiSuggestions'
import useFetchFileAiSuggestions from '@app/src/pages/Questionnaire/hooks/useFetchFileAiSuggestions'
import FileAiSuggestionDrawer from '@app/src/pages/Questionnaire/Provider/Report/SuggestedAnswers/FileAiSuggestionDrawer'
import PreviousAnswerDrawer from '@app/src/pages/Questionnaire/Provider/Report/SuggestedAnswers/PreviousAnswerDrawer'
import ScrapedAiSuggestionDrawer from '@app/src/pages/Questionnaire/Provider/Report/SuggestedAnswers/ScrapedAiSuggestionDrawer'
import { useReport } from '@app/src/pages/Questionnaire/ReportContext'
import { ReuseAllQuestionsStates } from '@app/src/pages/Questionnaire/ReportContextProvider'
import type { RequestItem } from '@app/src/types/resourceExplorer'
import { useQuery } from '@tanstack/react-query'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { getAvailableSources, getPreviousAnswerSource, SuggestionSource } from './suggestionSources'
import { suggestedAnswerButtonInViewQueryKey, suggestionIsSet } from './suggestionState'

export type QuestionSuggestionsCallbacks = {
  requestItem: RequestItem
  questionType: string
  isPreview: boolean
  onApplyPreviousAnswer: () => Promise<void>
  onApplyAiSuggestion: (suggestion: AiSuggestion) => void
  onAiSuggestionsFetched?: (suggestion: AiSuggestion) => void
  onIgnoreSuggestion: () => void
}

export type QuestionSuggestions = {
  observerRef: (node?: Element | null) => void
  sources: SuggestionSource[]
  isAutofilling: boolean
  isLoadingSuggestions: boolean
  openSuggestionDrawer: (source: SuggestionSource) => void
}

const useQuestionSuggestions = ({
  requestItem,
  questionType,
  isPreview,
  onApplyPreviousAnswer,
  onApplyAiSuggestion,
  onAiSuggestionsFetched,
  onIgnoreSuggestion,
}: QuestionSuggestionsCallbacks): QuestionSuggestions => {
  const { request, reuseAllQuestionsState } = useReport()
  const { previousAnswer, setAppliedSuggestion, sectionNumber, itemNumber } = useResponseItemContext()
  const { recordAutofill } = useProviderReport()
  const isAutofilling = reuseAllQuestionsState === ReuseAllQuestionsStates.ApplyAll
  const hasAutofilled = useRef(false)
  const { openDrawer, closeDrawer } = useDrawer()
  const { inView, ref: observerRef } = useInView()
  const [persistedInView, setPersistedInView] = useState(false)
  const [inViewReset, setInViewReset] = useState(true)

  useQuery({
    queryKey: [suggestedAnswerButtonInViewQueryKey, requestItem.id],
    queryFn: () => {
      setInViewReset(true)
      return true
    },
  })

  useEffect(() => {
    if (inViewReset) {
      setPersistedInView(inView)
      setInViewReset(false)
    } else if (inView) {
      setPersistedInView(true)
    }
  }, [inView, persistedInView, inViewReset])

  const { fetchedData: fileAiSuggestion, isLoadingFileAiSuggestions } = useFetchFileAiSuggestions(
    requestItem,
    questionType,
    isPreview,
    // Autofill covers the whole request, so it cannot wait for each question to be scrolled into view.
    persistedInView || isAutofilling,
    request?.periodName,
  )

  const documentSuggestion =
    suggestionIsSet(fileAiSuggestion, requestItem) && !isLoadingFileAiSuggestions ? fileAiSuggestion : undefined

  useEffect(() => {
    if (documentSuggestion) onAiSuggestionsFetched?.(documentSuggestion)
  }, [documentSuggestion, onAiSuggestionsFetched])

  const applyDocumentSuggestion = useCallback(
    (suggestion: AiSuggestion) => {
      setAppliedSuggestion({ source: SuggestionSource.Document, fileNames: suggestion.citations })
      onApplyAiSuggestion(suggestion)
    },
    [onApplyAiSuggestion, setAppliedSuggestion],
  )

  const applyPreviousAnswer = useCallback(async () => {
    if (!previousAnswer) return
    setAppliedSuggestion({ source: getPreviousAnswerSource(previousAnswer) })
    await onApplyPreviousAnswer()
  }, [previousAnswer, onApplyPreviousAnswer, setAppliedSuggestion])

  /* A document suggestion outranks a previous answer, which in turn already prefers a real previous
     answer over a scraped one. The ref keeps this to one pass per question: autofill mode stays on
     for the rest of the session, so without it, ignoring or clearing an answer would re-apply it. */
  useEffect(() => {
    if (!isAutofilling || hasAutofilled.current || isLoadingFileAiSuggestions) return
    if (!documentSuggestion && !previousAnswer) return

    hasAutofilled.current = true
    if (documentSuggestion) {
      applyDocumentSuggestion(documentSuggestion)
      recordAutofill(SuggestionSource.Document)
    } else if (previousAnswer) {
      applyPreviousAnswer()
      recordAutofill(getPreviousAnswerSource(previousAnswer))
    }
  }, [isAutofilling, isLoadingFileAiSuggestions, documentSuggestion, previousAnswer])

  const dismissSuggestion = useCallback(() => {
    setAppliedSuggestion(undefined)
    onIgnoreSuggestion()
    closeDrawer()
  }, [setAppliedSuggestion, onIgnoreSuggestion, closeDrawer])

  const openSuggestionDrawer = useCallback(
    (source: SuggestionSource) => {
      if (source === SuggestionSource.Document && documentSuggestion) {
        openDrawer(
          <FileAiSuggestionDrawer
            suggestion={documentSuggestion}
            requestItem={requestItem}
            questionType={questionType}
            onApply={applyDocumentSuggestion}
            onIgnore={dismissSuggestion}
            closeDrawer={closeDrawer}
            sectionNumber={sectionNumber}
            itemNumber={itemNumber}
          />,
        )
        return
      }

      if (!previousAnswer) return

      if (source === SuggestionSource.PublicData) {
        openDrawer(
          <ScrapedAiSuggestionDrawer
            previousAnswer={previousAnswer}
            requestItem={requestItem}
            questionType={questionType}
            onApply={applyPreviousAnswer}
            onIgnore={dismissSuggestion}
            closeDrawer={closeDrawer}
            sectionNumber={sectionNumber}
            itemNumber={itemNumber}
            provider={request?.targetObject}
          />,
        )
        return
      }

      openDrawer(
        <PreviousAnswerDrawer
          previousAnswer={previousAnswer}
          requestItem={requestItem}
          questionType={questionType}
          onApply={applyPreviousAnswer}
          onIgnore={dismissSuggestion}
          closeDrawer={closeDrawer}
          sectionNumber={sectionNumber}
          itemNumber={itemNumber}
        />,
      )
    },
    [
      documentSuggestion,
      previousAnswer,
      requestItem,
      questionType,
      applyDocumentSuggestion,
      applyPreviousAnswer,
      dismissSuggestion,
      sectionNumber,
      itemNumber,
      request?.targetObject,
    ],
  )

  return {
    observerRef,
    sources: getAvailableSources(previousAnswer, Boolean(documentSuggestion)),
    isAutofilling,
    isLoadingSuggestions: isLoadingFileAiSuggestions,
    openSuggestionDrawer,
  }
}

export default useQuestionSuggestions
